/**
 * HeadPoseRay — cast a 3D head-pose direction ray onto the paper plane.
 *
 * Replaces the legacy "sticky" cone (face-normal 2D projection + zone
 * classification) with a single precise ray → paper intersection point.
 *
 * Input: facial transformation matrix (from MediaPipe FaceLandmarker,
 *        `outputFacialTransformationMatrixes: true`) OR pre-computed
 *        yaw/pitch (degrees) as a fallback.
 * Output: (u, v) on paper in [0,1]² via homography, plus a 2D image
 *         point suitable for rendering the endpoint of the ray.
 *
 * This is intentionally a pure-math module so the same logic can move
 * into the Android/iOS SDK pure-logic layer later.
 */

import { applyHomography, type Point2D } from './Homography';

export interface RayOrigin {
  /** Eye midpoint in normalized image coords [0,1]. */
  x: number;
  y: number;
}

/** Minimal bbox shape matching `Detection.bboxNorm` / any AABB in [0,1] coords. */
export interface RayBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface RayHitObject<T> {
  /** The detection that the ray first entered. */
  detection: T;
  /** Parametric t along the ray segment at entry (0..1). */
  t: number;
}

/**
 * Optional auxiliary signals used to refine the primary head-pose ray.
 * These come from PoseLandmarker / FaceLandmarker and provide cross-checks
 * for Explainability — all available landmarks (nose, ears, shoulders)
 * contribute as supporting evidence rather than being dropped in favor
 * of a single signal.
 */
export interface RayAuxiliary {
  /** Shoulder tilt in degrees (from PoseLandmarker 11↔12). 0 = level. */
  shoulderAngleDeg?: number;
  /** Pose-derived yaw estimate (from ears/nose geometry). */
  poseYawDeg?: number;
  /** Pose-derived pitch estimate. */
  posePitchDeg?: number;
  /** Whether pose estimate is reliable (ears + nose visible). */
  poseValid?: boolean;
  /** Nose-tip landmark in normalized image coords, as secondary origin cue. */
  noseTip?: Point2D;
  /** Face-center (forehead/chin midpoint) for nose-offset validation. */
  faceCenter?: Point2D;
}

export interface HeadPoseRayResult {
  /** Endpoint on the image plane, normalized [0,1]. Always defined. */
  hitImage: Point2D;
  /** Paper coords [0,1]² if homography was supplied and ray lands on paper. */
  hitPaper: Point2D | null;
  /** True if the ray exits the webcam frame before hitting the paper. */
  offScreen: boolean;
  /** Length of the 2D projected direction vector (for UI opacity/width). */
  strength: number;
  /**
   * Agreement between FaceLandmarker yaw and pose-derived yaw.
   * 1 = perfect agreement, 0 = ≥45° divergence, null if pose unavailable.
   * Surfaced to the overlay so reviewers can see when signals disagree.
   */
  agreement: number | null;
  /**
   * Confidence score in [0,1] combining face-pose strength and pose agreement.
   * Intended for overlay opacity / evidence logging.
   */
  confidence: number;
}

/**
 * Cast a ray from the eye midpoint along the head-pose direction and
 * project onto the image plane. If `homography` is provided, also map
 * the hit point into paper coordinates.
 *
 * The projection length is scaled by image height — we do NOT model the
 * true 3D distance to paper because that would require camera calibration
 * (focal length, head-to-camera distance). The linear approximation is
 * sufficient for zone-level evidence and matches the existing cone scale.
 *
 * @param yawDeg   positive = face turned to viewer's right (image-right).
 * @param pitchDeg positive = face tilted up.
 * @param origin   eye midpoint in normalized image coords [0,1].
 * @param aspect   image height / image width (to de-skew angular scaling).
 * @param homography optional 3×3 matrix mapping image → paper.
 */
export interface HeadPoseRayCast extends HeadPoseRayResult {
  /** Parametric t at which the ray exits the image boundary (unit dir). */
  tMax: number;
  /** Unit direction used after aux corrections (for caller object test). */
  dirX: number;
  dirY: number;
  /** Origin actually used (may include nose-offset nudge). */
  originUsed: Point2D;
}

/**
 * Cast a ray from `origin` along `(dirX, dirY)` and extend until it exits
 * the image boundary. Caller supplies the direction as an image-space
 * vector — typically `nose − faceCenter` from FaceLandmarker, which is
 * already in the same coord frame as the rendered overlay and avoids the
 * sign flips introduced by Euler conversion.
 *
 * `dirX`/`dirY` need NOT be unit length — the function normalizes them.
 * They must be in the same normalized [0,1] image coords as `origin`.
 *
 * `aux.shoulderAngleDeg` still applies a small roll correction, and the
 * nose-vs-faceCenter offset still nudges the origin by a small amount.
 */
export function castHeadPoseRay(
  dirX: number,
  dirY: number,
  origin: RayOrigin,
  homography: Float64Array | null,
  aux?: RayAuxiliary,
): HeadPoseRayCast {
  // Normalize direction
  const mag = Math.hypot(dirX, dirY);
  const strength = Math.min(1, mag * 4); // scale for UI opacity
  let ux = mag > 1e-6 ? dirX / mag : 0;
  let uy = mag > 1e-6 ? dirY / mag : 1;

  // Shoulder roll correction — capped to avoid runaway when pose is flaky.
  if (aux?.shoulderAngleDeg !== undefined && Math.abs(aux.shoulderAngleDeg) > 2) {
    const clamped = Math.max(-20, Math.min(20, aux.shoulderAngleDeg));
    const rollRad = (clamped * 0.35 * Math.PI) / 180;
    const c = Math.cos(rollRad), s = Math.sin(rollRad);
    const nux = c * ux - s * uy;
    const nuy = s * ux + c * uy;
    ux = nux;
    uy = nuy;
  }

  // Nose-offset nudge (≤ a few % of image) — sharpens origin mid-turn.
  let ox = origin.x;
  let oy = origin.y;
  if (aux?.noseTip && aux?.faceCenter) {
    ox += (aux.noseTip.x - aux.faceCenter.x) * 0.1;
    oy += (aux.noseTip.y - aux.faceCenter.y) * 0.1;
  }

  // Solve for the largest t > 0 where (ox + t*ux, oy + t*uy) is still
  // inside [0,1]². This extends the ray to the full visible extent so
  // object-ray intersection can find detections anywhere on screen.
  let tMax = Infinity;
  if (ux > 1e-9)       tMax = Math.min(tMax, (1 - ox) / ux);
  else if (ux < -1e-9) tMax = Math.min(tMax, (0 - ox) / ux);
  if (uy > 1e-9)       tMax = Math.min(tMax, (1 - oy) / uy);
  else if (uy < -1e-9) tMax = Math.min(tMax, (0 - oy) / uy);
  if (!isFinite(tMax) || tMax <= 0) tMax = 0.001;

  const hitImage: Point2D = { x: ox + ux * tMax, y: oy + uy * tMax };

  let hitPaper: Point2D | null = null;
  if (homography) {
    hitPaper = applyHomography(homography, hitImage);
  }

  // Off-screen never applies here — ray ends ON the image boundary by
  // construction. Kept for API compatibility; surface "grazing edge"
  // cases if the raw direction was already past the boundary at t=0.
  const offScreen = false;

  let agreement: number | null = null;
  if (aux?.poseValid && aux.poseYawDeg !== undefined) {
    // Derive a rough yaw from the 2D direction: arctangent of x over the
    // forward component. Positive dirX = face points image-right (same
    // convention as the pose yaw sign passed in by the caller).
    const approxYawDeg = Math.atan2(dirX, Math.max(0.001, Math.abs(dirY))) * (180 / Math.PI);
    const diff = Math.abs(approxYawDeg - aux.poseYawDeg);
    agreement = Math.max(0, 1 - diff / 45);
  }
  const confidence = agreement !== null
    ? 0.6 + 0.4 * (0.5 * strength + 0.5 * agreement)
    : 0.6 + 0.4 * strength;

  return {
    hitImage,
    hitPaper,
    offScreen,
    strength,
    agreement,
    confidence,
    tMax,
    dirX: ux,
    dirY: uy,
    originUsed: { x: ox, y: oy },
  };
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/**
 * Find the first detection bbox the ray enters, measured from `origin`.
 * Returns null if no bbox is crossed. Uses the slab method on an AABB.
 *
 * `excludeContains` is the face/self region in the same normalized coords;
 * any bbox containing this point is skipped (matches legacy cone behavior
 * of ignoring the "person" class that covers the student themselves).
 *
 * `t` is in [0, tMax] where origin+t·(hit−origin) is the ray segment.
 */
export function firstObjectAlongRay<T extends { bboxNorm: RayBox }>(
  origin: Point2D,
  dirX: number,
  dirY: number,
  tLimit: number,
  detections: readonly T[],
  excludeContains?: Point2D,
): RayHitObject<T> | null {
  if (Math.hypot(dirX, dirY) < 1e-6) return null;

  let best: RayHitObject<T> | null = null;

  for (const det of detections) {
    const bb = det.bboxNorm;
    if (
      excludeContains &&
      excludeContains.x >= bb.x && excludeContains.x <= bb.x + bb.w &&
      excludeContains.y >= bb.y && excludeContains.y <= bb.y + bb.h
    ) {
      continue;
    }

    // Slab method. tEnter = the earliest t at which the ray is inside
    // both axis slabs of the bbox.
    const invDx = Math.abs(dirX) > 1e-9 ? 1 / dirX : Infinity;
    const invDy = Math.abs(dirY) > 1e-9 ? 1 / dirY : Infinity;

    const tx1 = (bb.x - origin.x) * invDx;
    const tx2 = (bb.x + bb.w - origin.x) * invDx;
    const ty1 = (bb.y - origin.y) * invDy;
    const ty2 = (bb.y + bb.h - origin.y) * invDy;

    const tEnter = Math.max(Math.min(tx1, tx2), Math.min(ty1, ty2));
    const tExit  = Math.min(Math.max(tx1, tx2), Math.max(ty1, ty2));

    if (tExit < 0 || tEnter > tExit) continue;   // ray misses bbox
    if (tEnter > tLimit) continue;                // bbox lies past the ray
    const entry = Math.max(tEnter, 0);

    if (!best || entry < best.t) {
      best = { detection: det, t: entry };
    }
  }

  return best;
}
